import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, homeFor } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import Tasks from "@/pages/Tasks";
import Maintenance from "@/pages/Maintenance";
import WorkOrders from "@/pages/WorkOrders";
import Messages from "@/pages/Messages";
import Lettings from "@/pages/Lettings";
import ApplicantPortal from "@/pages/ApplicantPortal";
import SetPassword from "@/pages/SetPassword";
import ResetPassword from "@/pages/ResetPassword";
import Sales from "@/pages/Sales";
import Landlords from "@/pages/Landlords";
import Contacts from "@/pages/Contacts";
import Projects from "@/pages/Projects";
import Workflows from "@/pages/Workflows";
import Finance from "@/pages/Finance";
import Documents from "@/pages/Documents";
import Notifications from "@/pages/Notifications";
import Settings from "@/pages/Settings";
import LandlordPortal from "@/pages/LandlordPortal";
import TenantPortal from "@/pages/TenantPortal";
import ContractorPortal from "@/pages/ContractorPortal";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-10 h-10 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
    </div>
  );
}

function RoleRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function LoginGate() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to={homeFor(user.role)} replace />;
  return <Login />;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  return <Navigate to={user ? homeFor(user.role) : "/login"} replace />;
}

const STAFF = ["admin", "staff"];

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<LoginGate />} />

      {/* Staff / Admin */}
      <Route path="/dashboard" element={<RoleRoute roles={STAFF}><Dashboard /></RoleRoute>} />
      <Route path="/properties" element={<RoleRoute roles={STAFF}><Properties /></RoleRoute>} />
      <Route path="/landlords" element={<RoleRoute roles={STAFF}><Landlords /></RoleRoute>} />
      <Route path="/contacts" element={<RoleRoute roles={STAFF}><Contacts /></RoleRoute>} />
      <Route path="/properties/:id" element={<RoleRoute roles={STAFF}><PropertyDetail /></RoleRoute>} />
      <Route path="/tasks" element={<RoleRoute roles={STAFF}><Tasks /></RoleRoute>} />
      <Route path="/maintenance" element={<RoleRoute roles={STAFF}><Maintenance /></RoleRoute>} />
      <Route path="/work-orders" element={<RoleRoute roles={STAFF}><WorkOrders /></RoleRoute>} />
      <Route path="/lettings" element={<RoleRoute roles={STAFF}><Lettings /></RoleRoute>} />
      <Route path="/sales" element={<RoleRoute roles={STAFF}><Sales /></RoleRoute>} />
      <Route path="/messages" element={<RoleRoute roles={["admin"]}><Messages /></RoleRoute>} />
      <Route path="/projects" element={<RoleRoute roles={STAFF}><Projects /></RoleRoute>} />
      <Route path="/workflows" element={<RoleRoute roles={STAFF}><Workflows /></RoleRoute>} />
      <Route path="/finance" element={<RoleRoute roles={STAFF}><Finance /></RoleRoute>} />
      <Route path="/documents" element={<RoleRoute roles={STAFF}><Documents /></RoleRoute>} />
      <Route path="/notifications" element={<RoleRoute roles={STAFF}><Notifications /></RoleRoute>} />
      <Route path="/settings" element={<RoleRoute roles={["admin"]}><Settings /></RoleRoute>} />

      {/* Portals */}
      <Route path="/landlord" element={<RoleRoute roles={["landlord"]}><LandlordPortal /></RoleRoute>} />
      <Route path="/tenant" element={<RoleRoute roles={["tenant"]}><TenantPortal /></RoleRoute>} />
      <Route path="/contractor" element={<RoleRoute roles={["contractor"]}><ContractorPortal /></RoleRoute>} />

      {/* Public applicant referencing portal (no auth) */}
      <Route path="/apply/:token" element={<ApplicantPortal />} />
      {/* Public set-password / account activation */}
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
